import { useCallback, useState } from 'react';
import { Box, Dialog } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import { SBase, SEmptyState, SInfiniteTable } from '@setvi/shared/components';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import { ProductDetails } from 'componentsV2';
import { useProductStyles } from 'pages/products';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import { ProductActionType } from 'pages/products/utils';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';
import { ProductsSearch } from './products-search';
import { useSelectedProductsColumns } from './use-selected-products-columns';

type ProductWithTempId = ProductDTO & { tempId: string };

interface AddBySkuComponentProps {
  selectedProducts: ProductWithTempId[];
  selectedProduct: ProductDTO | null;
  setSelectedProduct: (product: ProductDTO | null) => void;
  captureAnalytics: (analytics: any, module: AnalyticsModules) => void;
  openProductDetails: (product: ProductDTO) => void;
  handleAddProducts: (product: ProductDTO) => void;
  handleRemoveProduct: (tempId: string) => void;
  handleUpdateProduct: (
    product: ProductWithTempId,
    newValues: Partial<ProductDTO>
  ) => void;
}

const AddBySkuComponent = ({
  selectedProducts,
  selectedProduct,
  setSelectedProduct,
  captureAnalytics,
  openProductDetails,
  handleAddProducts,
  handleRemoveProduct,
  handleUpdateProduct
}: AddBySkuComponentProps) => {
  const { companyData } = useAppContext();
  const userType = companyData.Settings[CompanySettingsTypeID.CompanyType]
    ?.Value as CompanyProductUserType;
  const { columns } = useSelectedProductsColumns(
    handleRemoveProduct,
    handleUpdateProduct
  );

  const classes = useProductStyles();

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
        <ProductsSearch onProductClick={handleAddProducts} />
      </Box>

      {selectedProducts?.length > 0 ? (
        <Box
          style={{
            overflow: 'auto'
          }}>
          <SInfiniteTable
            list={selectedProducts}
            columns={columns}
            keyGetter={(item: ProductWithTempId) => item.tempId}
            row={{
              onClick: item => openProductDetails(item)
            }}
          />
        </Box>
      ) : (
        <SEmptyState
          titleStyle={{
            color: '#5D7083',
            fontWeight: 600,
            fontSize: '14px'
          }}
          additionalContent={
            <Box
              style={{
                maxWidth: 260,
                textAlign: 'center',
                color: '#5D7083',
                fontSize: '14px'
              }}>
              Type a product SKU in the field above to find and add it to your
              quote.
            </Box>
          }
          imageSrc="/images/empty-state-add-by-sku.png"
          title="No Products added yet"
        />
      )}

      <Dialog
        maxWidth="md"
        aria-labelledby="product-details-dialog"
        open={!!selectedProduct}
        style={{ zIndex: '1300' }}
        onKeyDown={event => {
          if (event.key === 'Escape') setSelectedProduct(null);
        }}
        classes={{ paper: classes.dialogPaper }}>
        <ProductDetails
          selectedProduct={selectedProduct || {}}
          selectedCustomerId={undefined}
          userType={userType}
          canSelectProducts={false}
          captureResourceAnalytics={() =>
            selectedProduct &&
            captureAnalytics(
              {
                productSKU: selectedProduct.sku,
                actionType: ProductActionType.AssetView
              },
              AnalyticsModules.QuotesCatalog
            )
          }
          openProductDetails={openProductDetails}
          onClose={() => setSelectedProduct(null)}
          readOnly
        />
      </Dialog>
    </>
  );
};

interface AddBySkuProps {
  parentId?: string;
  onClose(): void;
  addProducts: (params: {
    products: ProductDTO[];
    parentId?: string | null;
  }) => Promise<any>;
  isAddingProducts: boolean;
}

export const AddBySku = ({
  parentId,
  onClose,
  addProducts,
  isAddingProducts
}: AddBySkuProps) => {
  const [selectedProducts, setProducts] = useState<ProductWithTempId[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductDTO | null>(
    null
  );
  const { captureAnalytics } = useAnalytics();

  const openProductDetails = (product: ProductDTO) => {
    setSelectedProduct(product);

    captureAnalytics(
      {
        productSKU: product.sku,
        actionType: ProductActionType.DetailsView
      },
      AnalyticsModules.QuotesCatalog
    );
  };

  const handleConfirm = useCallback(() => {
    addProducts({
      products: selectedProducts,
      parentId: parentId || null
    });
    onClose();
  }, [addProducts, onClose, parentId, selectedProducts]);

  const handleRemoveProduct = useCallback((tempId: string) => {
    setProducts(prev => prev.filter(item => item.tempId !== tempId));
  }, []);

  const handleAddProducts = useCallback((prod: ProductDTO) => {
    setProducts(prev => [...prev, { ...prod, tempId: uuidv4() }]);
  }, []);

  const handleUpdateProduct = useCallback(
    (product: ProductWithTempId, newValues: Partial<ProductDTO>) => {
      setProducts(prevProducts =>
        prevProducts.map(prod =>
          prod.tempId === product.tempId ? { ...prod, ...newValues } : prod
        )
      );
    },
    []
  );

  return (
    <SBase
      header={{ title: 'Add by SKU', onClose }}
      footer={{
        onConfirm: handleConfirm,
        onClose,
        confirmLabel: `Add Product${selectedProducts?.length > 1 ? 's' : ''} (${selectedProducts?.length})`,
        closeLabel: 'Cancel',
        disableConfirm: selectedProducts?.length === 0,
        confirmLoading: isAddingProducts
      }}
      width="70vw"
      height="70vh">
      <AddBySkuComponent
        selectedProducts={selectedProducts}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        captureAnalytics={captureAnalytics}
        openProductDetails={openProductDetails}
        handleRemoveProduct={handleRemoveProduct}
        handleAddProducts={handleAddProducts}
        handleUpdateProduct={handleUpdateProduct}
      />
    </SBase>
  );
};
